import { ModuleDocContent } from "./docsData";

export const analyticsModuleDocs: Record<string, ModuleDocContent> = {
  "analytics-overview": {
    title: "Analytics Overview",
    subtitle: "Employee Learning Analytics",
    color: "emerald",
    badge: "Analytics & Reports",
    description:
      "High-level analytics dashboard with two tabs (Course and Certification). View 6 metric cards, department performance, learning trends, top performers, training status, and upcoming deadlines. Filter by time period.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p><strong>Analytics Overview</strong> provides a comprehensive dashboard for tracking employee learning across courses and certifications. Toggle between Course and Certification views using the tab switch in the header.</p>
      </div>

      <section>
        <h2>Tabs</h2>
        <p>A toggle switch in the top-right of the header lets you switch between two views:</p>
        <table>
          <thead>
            <tr>
              <th>Tab</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Course</strong></td><td>Course-based analytics: enrollments, completions, and training metrics.</td></tr>
            <tr><td><strong>Certificate</strong></td><td>Certification-based analytics: pass rates, compliance, and certification metrics.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Time Range Filter</h2>
        <p>Filter all data by time period: <strong>Week</strong>, <strong>Month</strong>, <strong>Quarter</strong>, <strong>Year</strong>, or <strong>Custom</strong> (with From and To date pickers).</p>
      </section>

      <section>
        <h2>Metrics Cards</h2>
        <p>Six summary cards with trend indicators (period-over-period change):</p>
        <table>
          <thead>
            <tr>
              <th>Card</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Total Employees</strong></td><td>Total employee count across all departments.</td></tr>
            <tr><td><strong>Active Learning Rate</strong></td><td>Percentage of employees actively learning, with active learner count.</td></tr>
            <tr><td><strong>Avg Completion Rate</strong></td><td>Average completion percentage across all courses.</td></tr>
            <tr><td><strong>Training ROI</strong></td><td>Return on investment shown as a multiplier (e.g., 3.2x).</td></tr>
            <tr><td><strong>Compliance Rate</strong></td><td>Percentage of mandatory training completed.</td></tr>
            <tr><td><strong>Avg Time to Complete</strong></td><td>Average time per course (lower is better).</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Dashboard Sections</h2>
        <table>
          <thead>
            <tr>
              <th>Section</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Department Performance</strong></td>
              <td>Horizontal bar chart showing top 5 departments by completion rate. Each bar displays employees count, active rate, completion rate, avg score, and time spent.</td>
            </tr>
            <tr>
              <td><strong>Learning Trends</strong></td>
              <td>Composed chart (bars + lines) with toggle between Enrollments, Completions, and Training Time views over the selected time period.</td>
            </tr>
            <tr>
              <td><strong>Top Performers</strong></td>
              <td>Top 5 employees ranked by courses completed, showing department, avg score, completion rate, and rank.</td>
            </tr>
            <tr>
              <td><strong>Training Status</strong></td>
              <td>Counts of Completed, In Progress, Not Started, and Overdue assignments.</td>
            </tr>
            <tr>
              <td><strong>Upcoming Deadlines</strong></td>
              <td>Paginated table of upcoming training deadlines with priority badges (high/medium/low). Click a row to see assigned users and their individual deadlines.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Common use cases</h2>
        <div class="sz-use-cases">
          <div class="sz-use-case">
            <div class="sz-use-case-title">Executive summary</div>
            <div class="sz-use-case-flow">
              Set the time filter to Quarter. Review the 6 metric cards for headline numbers. Check Compliance Rate and Upcoming Deadlines for any high-priority items needing attention.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Department performance review</div>
            <div class="sz-use-case-flow">
              Review the Department Performance chart to compare completion rates across teams. Toggle Learning Trends between Enrollments and Completions to understand engagement patterns over time.
            </div>
          </div>
        </div>
      </section>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/6c4ab565-ba07-436d-ab2a-21681c9c349d.png" alt="Analytics Overview" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Analytics Overview dashboard</p></div>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Tip</div>
        <p>Toggle between the <strong>Course</strong> and <strong>Certificate</strong> tabs to get a complete picture of employee learning. Use the <strong>Custom</strong> date range to compare specific periods.</p>
      </div>

    </div>`,
  },

  "course-analytics": {
    title: "Course Performance",
    subtitle: "Track Course Performance",
    color: "emerald",
    badge: "Analytics & Reports",
    description:
      "Monitor course performance with stats cards for total courses, enrollments, completion rate, and active learners. Filter by time range, category, status, department, and role. View a detailed courses table with export.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p><strong>Course Performance</strong> provides a table-based analytics view of all courses with enrollment and completion metrics. Filter by multiple dimensions and export data for reporting.</p>
      </div>

      <section>
        <h2>Stats Cards</h2>
        <p>Four summary cards with trend indicators:</p>
        <table>
          <thead>
            <tr>
              <th>Card</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Total Courses</strong></td><td>Count of all courses with trend change.</td></tr>
            <tr><td><strong>Total Enrollments</strong></td><td>Total learner enrollments with trend.</td></tr>
            <tr><td><strong>Avg Completion Rate</strong></td><td>Average completion percentage with trend.</td></tr>
            <tr><td><strong>Active Learners</strong></td><td>Currently active learners with trend.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Filters</h2>
        <table>
          <thead>
            <tr>
              <th>Filter</th>
              <th>Options</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Time Range</strong></td><td>All Time, Weekly, Monthly, Quarterly, Yearly, Custom (with date pickers).</td></tr>
            <tr><td><strong>Search</strong></td><td>Search by course name.</td></tr>
            <tr><td><strong>Category</strong></td><td>Dropdown populated from API.</td></tr>
            <tr><td><strong>Status</strong></td><td>Published, Unpublished, Draft.</td></tr>
            <tr><td><strong>Department</strong></td><td>Searchable dropdown.</td></tr>
            <tr><td><strong>Role</strong></td><td>Dependent on department selection, searchable.</td></tr>
          </tbody>
        </table>
        <p>Collapsible filter section. Active filters display as removable chips.</p>
      </section>

      <section>
        <h2>Courses Table</h2>
        <table>
          <thead>
            <tr>
              <th>Column</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Course</strong></td><td>Thumbnail image and title.</td></tr>
            <tr><td><strong>Enrollments</strong></td><td>Total enrollment count.</td></tr>
            <tr><td><strong>Completed Users</strong></td><td>Number of users who finished.</td></tr>
            <tr><td><strong>Completion %</strong></td><td>Color-coded progress bar: green &ge;80%, yellow &ge;60%, red &lt;60%.</td></tr>
            <tr><td><strong>Actions</strong></td><td>View enrolled users (drill-down to user-level view).</td></tr>
          </tbody>
        </table>
        <p>Export to Excel or CSV (permission-gated).</p>
      </section>

      <section>
        <h2>Common use cases</h2>
        <div class="sz-use-cases">
          <div class="sz-use-case">
            <div class="sz-use-case-title">Identify low-completion courses</div>
            <div class="sz-use-case-flow">
              Filter by Published status and sort by Completion %. Courses with red progress bars (below 60%) need attention. Click the Actions link to see which specific users haven't completed.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Department-specific course tracking</div>
            <div class="sz-use-case-flow">
              Select a department from the filter and review enrollment and completion data. Export to Excel for department-level reporting.
            </div>
          </div>
        </div>
      </section>


      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/d5fc36dd-dcaf-4334-a5a1-0651f021aeda.png" alt="Course Performance" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Course Performance analytics</p></div>

       <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Tip</div>
        <p>Use the time range filter to compare course performance across different periods. Export data regularly for trend analysis.</p>
      </div>

    </div>`,
  },

  "cert-analytics": {
    title: "Certification Performance",
    subtitle: "Certification Performance Insights",
    color: "emerald",
    badge: "Analytics & Reports",
    description:
      "Monitor certification performance with stats cards for total certifications, enrollments, pass rate, and active learners. Filter by time range, status, department, and role. View a detailed certifications table with export.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p><strong>Certification Performance</strong> provides a table-based analytics view of all certifications with enrollment, pass rate, and score metrics. Filter by multiple dimensions and export data for reporting.</p>
      </div>

      <section>
        <h2>Stats Cards</h2>
        <p>Four summary cards with trend indicators:</p>
        <table>
          <thead>
            <tr>
              <th>Card</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Total Certifications</strong></td><td>Count of all certifications with trend change.</td></tr>
            <tr><td><strong>Total Enrollments</strong></td><td>Total learner enrollments with trend.</td></tr>
            <tr><td><strong>Avg Pass Rate</strong></td><td>Average pass rate percentage with trend.</td></tr>
            <tr><td><strong>Active Learners</strong></td><td>Currently active learners with trend.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Filters</h2>
        <table>
          <thead>
            <tr>
              <th>Filter</th>
              <th>Options</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Time Range</strong></td><td>All Time, Weekly, Monthly, Quarterly, Yearly, Custom (with date pickers).</td></tr>
            <tr><td><strong>Search</strong></td><td>Search by certification name.</td></tr>
            <tr><td><strong>Status</strong></td><td>Published, Unpublished.</td></tr>
            <tr><td><strong>Department</strong></td><td>Searchable dropdown.</td></tr>
            <tr><td><strong>Role</strong></td><td>Dependent on department selection, searchable.</td></tr>
          </tbody>
        </table>
        <p>Collapsible filter section. Active filters display as removable chips.</p>
      </section>

      <section>
        <h2>Certifications Table</h2>
        <table>
          <thead>
            <tr>
              <th>Column</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Certification</strong></td><td>Thumbnail image and title.</td></tr>
            <tr><td><strong>Enrollments</strong></td><td>Total enrollment count.</td></tr>
            <tr><td><strong>Passed Users</strong></td><td>Number of users who passed.</td></tr>
            <tr><td><strong>Pass Rate %</strong></td><td>Color-coded progress bar: green &ge;80%, yellow &ge;60%, red &lt;60%.</td></tr>
            <tr><td><strong>Avg Score</strong></td><td>Average exam score.</td></tr>
            <tr><td><strong>Actions</strong></td><td>View performance (drill-down to user-level view).</td></tr>
          </tbody>
        </table>
        <p>Export to Excel or CSV (permission-gated).</p>
      </section>

      <section>
        <h2>Common use cases</h2>
        <div class="sz-use-cases">
          <div class="sz-use-case">
            <div class="sz-use-case-title">Identify low pass-rate certifications</div>
            <div class="sz-use-case-flow">
              Filter by Published status and sort by Pass Rate %. Certifications with red bars may have overly difficult exams or insufficient prerequisite courses. Click Actions to review individual user performance.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Role-specific certification tracking</div>
            <div class="sz-use-case-flow">
              Select a department and role from the filters to see which certifications are being pursued by specific roles. Export the data for compliance reporting.
            </div>
          </div>
        </div>
      </section>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Tip</div>
        <p>Compare pass rates across time periods using the time range filter to spot trends in certification difficulty or preparation quality.</p>
      </div>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/c84aa88b-aea6-4899-8e1b-c3b3fe7c8151.png" alt="Certification Performance" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Certification Performance analytics</p></div>

    </div>`,
  },

  "lp-analytics": {
    title: "Learning Path Performance",
    subtitle: "Learning Path Performance",
    color: "emerald",
    badge: "Analytics & Reports",
    description:
      "Analyze learning path effectiveness with enrollment, completion rate, and active learner metrics. Filter by time range, category, status, department, and role with Excel/CSV export.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p><strong>Learning Path Performance Analytics</strong> provides insights into learning path effectiveness, engagement metrics, and areas for improvement. Filter by multiple dimensions and export data for reporting.</p>
      </div>

      <section>
        <h2>Metrics Cards</h2>
        <p>Four summary cards with trend indicators:</p>
        <table>
          <thead>
            <tr>
              <th>Card</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Total Learning Paths</strong></td>
              <td>Count with trend change from previous period.</td>
            </tr>
            <tr>
              <td><strong>Total Enrollments</strong></td>
              <td>Total learner enrollments with trend.</td>
            </tr>
            <tr>
              <td><strong>Avg Completion Rate</strong></td>
              <td>Average completion percentage with trend.</td>
            </tr>
            <tr>
              <td><strong>Active Learners</strong></td>
              <td>Currently active learners with trend.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Filters</h2>
        <table>
          <thead>
            <tr>
              <th>Filter</th>
              <th>Options</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Time Range</strong></td>
              <td>All Time, Week, Month, Quarter, Year, Custom (with date pickers).</td>
            </tr>
            <tr>
              <td><strong>Search</strong></td>
              <td>Search by learning path name.</td>
            </tr>
            <tr>
              <td><strong>Category</strong></td>
              <td>Dropdown populated from API.</td>
            </tr>
            <tr>
              <td><strong>Status</strong></td>
              <td>Published, Unpublished, Draft.</td>
            </tr>
            <tr>
              <td><strong>Department</strong></td>
              <td>Searchable dropdown.</td>
            </tr>
            <tr>
              <td><strong>Role</strong></td>
              <td>Dependent on department selection, searchable.</td>
            </tr>
          </tbody>
        </table>
        <p>Active filters display as removable chips below the filter bar.</p>
      </section>

      <section>
        <h2>Learning Paths Table</h2>
        <table>
          <thead>
            <tr>
              <th>Column</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Learning Path</strong></td>
              <td>Thumbnail image and title.</td>
            </tr>
            <tr>
              <td><strong>Enrollments</strong></td>
              <td>Total enrollment count.</td>
            </tr>
            <tr>
              <td><strong>Completed Users</strong></td>
              <td>Number of users who finished.</td>
            </tr>
            <tr>
              <td><strong>Completion %</strong></td>
              <td>Color-coded progress bar: green &ge;80%, yellow &ge;60%, red &lt;60%.</td>
            </tr>
            <tr>
              <td><strong>Total Courses</strong></td>
              <td>Number of courses in the path.</td>
            </tr>
            <tr>
              <td><strong>Actions</strong></td>
              <td>View courses link (drill-down to course-level user view).</td>
            </tr>
          </tbody>
        </table>
        <p>Export to Excel or CSV (permission-gated).</p>
      </section>

      <section>
        <h2>Common use cases</h2>
        <div class="sz-use-cases">
          <div class="sz-use-case">
            <div class="sz-use-case-title">Evaluate path effectiveness</div>
            <div class="sz-use-case-flow">
              Filter by Published status and sort by Completion %. Paths with low completion may need course adjustments. Click the Actions link to see which specific courses learners drop off at.
            </div>
          </div>
        </div>
      </section>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/85eff74a-45c3-47ef-a1ef-f297e488094c.png" alt="Learning Path Performance" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Learning Path Performance analytics</p></div>

    </div>`,
  },

  "employee-progress": {
    title: "Employee Progress",
    subtitle: "Individual Learning Tracking",
    color: "emerald",
    badge: "Analytics & Reports",
    description:
      "Track individual employee learning progress with extensive filters for course, certification, status, engagement, department, role, and organizational hierarchy. View a detailed 15-column table with export.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p><strong>Employee Learning Progress</strong> tracks individual employee progress across courses and certifications. Use extensive filters including organizational hierarchy (BU Head, Reporting Manager, HRBP) to narrow down specific learners.</p>
      </div>

      <section>
        <h2>Filters</h2>
        <table>
          <thead>
            <tr>
              <th>Filter</th>
              <th>Options</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Search</strong></td>
              <td>By name, email, or employee ID.</td>
            </tr>
            <tr>
              <td><strong>Course</strong></td>
              <td>Specific course dropdown.</td>
            </tr>
            <tr>
              <td><strong>Certification</strong></td>
              <td>Specific certification dropdown.</td>
            </tr>
            <tr>
              <td><strong>Status</strong></td>
              <td>All, Not Started, In Progress, Completed, Overdue.</td>
            </tr>
            <tr>
              <td><strong>Engagement</strong></td>
              <td>All, High, Medium, Low.</td>
            </tr>
            <tr>
              <td><strong>Department &amp; Role</strong></td>
              <td>Department dropdown with cascading Role filter.</td>
            </tr>
            <tr>
              <td><strong>Org Hierarchy</strong></td>
              <td>BU Head, Reporting Manager, HRBP (email-based dropdowns).</td>
            </tr>
            <tr>
              <td><strong>Date Ranges</strong></td>
              <td>Course assigned/completed and Certification assigned/completed date ranges.</td>
            </tr>
            <tr>
              <td><strong>Sort By</strong></td>
              <td>Assigned Courses, Completed %, Completed Count, Cert Count, Completed Cert Count.</td>
            </tr>
            <tr>
              <td><strong>Assigned Only</strong></td>
              <td>Toggle to hide users with zero assignments.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Table Columns</h2>
        <table>
          <thead>
            <tr>
              <th>Column</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Employee</strong></td><td>Avatar, name, email.</td></tr>
            <tr><td><strong>Employee ID</strong></td><td>Unique identifier.</td></tr>
            <tr><td><strong>Department</strong></td><td>Clickable (shows multi-department modal if multiple).</td></tr>
            <tr><td><strong>Courses Assigned</strong></td><td>Total assigned course count.</td></tr>
            <tr><td><strong>Completed Courses</strong></td><td>Count of completed courses.</td></tr>
            <tr><td><strong>Progress</strong></td><td>Visual progress bar.</td></tr>
            <tr><td><strong>Completion %</strong></td><td>Completion percentage.</td></tr>
            <tr><td><strong>Time Spent (Course)</strong></td><td>Total time in HH:MM:SS format.</td></tr>
            <tr><td><strong>Certs Assigned</strong></td><td>Certification assignment count.</td></tr>
            <tr><td><strong>Certs Completed</strong></td><td>Completed certification count.</td></tr>
            <tr><td><strong>Time Spent (Cert)</strong></td><td>Total cert time in HH:MM:SS.</td></tr>
            <tr><td><strong>Streak</strong></td><td>Learning streak days.</td></tr>
            <tr><td><strong>Engagement</strong></td><td>High/Medium/Low color-coded badge.</td></tr>
            <tr><td><strong>Last Active</strong></td><td>Last activity date.</td></tr>
            <tr><td><strong>Actions</strong></td><td>View details link.</td></tr>
          </tbody>
        </table>
        <p>Export to Excel or CSV (permission-gated).</p>
      </section>

      <section>
        <h2>Common use cases</h2>
        <div class="sz-use-cases">
          <div class="sz-use-case">
            <div class="sz-use-case-title">Identify at-risk learners</div>
            <div class="sz-use-case-flow">
              Filter by Status "Overdue" and Engagement "Low" to find learners who are behind and disengaged. Use the org hierarchy filters to narrow by specific manager's team.
            </div>
          </div>
        </div>
      </section>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/3c31168d-3aba-4cfe-93f1-ca79c3923bd5.png" alt="Employee Progress" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Employee Progress tracking</p></div>

    </div>`,
  },

  "my-team-progress": {
    title: "My Team Progress",
    subtitle: "Team Learning Monitoring",
    color: "emerald",
    badge: "Analytics & Reports",
    description:
      "Monitor your team's learning journey filtered by your organizational relationship (BU Head, Reporting Manager, HRBP). Includes full filters, a 15-column table, and export.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p><strong>My Team Progress</strong> focuses on your direct reports and team members. A <strong>Team View Filter</strong> lets you filter by organizational relationship: All, Business Unit, Direct Reports, or HR Business Partner.</p>
      </div>

      <section>
        <h2>Team View Filter</h2>
        <p>Unique to this page &mdash; restricts results by your organizational relationship:</p>
        <table>
          <thead>
            <tr>
              <th>Option</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>All Team Members</strong></td><td>Show all team members across all relationships.</td></tr>
            <tr><td><strong>Business Unit</strong></td><td>Show users where you are the BU Head.</td></tr>
            <tr><td><strong>Direct Reports</strong></td><td>Show your direct reports (Reporting Manager).</td></tr>
            <tr><td><strong>HR Business Partner</strong></td><td>Show users where you are the HRBP.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Filters</h2>
        <p>Primary filters are always visible. Additional filters are behind a collapsible <strong>Show Filters</strong> toggle.</p>
        <table>
          <thead>
            <tr>
              <th>Filter</th>
              <th>Options</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Search</strong></td><td>By name, email, or employee ID.</td></tr>
            <tr><td><strong>Course</strong></td><td>Specific course dropdown with search.</td></tr>
            <tr><td><strong>Certification</strong></td><td>Specific certification dropdown with search.</td></tr>
            <tr><td><strong>Department</strong></td><td>Department dropdown (in expanded filters).</td></tr>
            <tr><td><strong>Role</strong></td><td>Cascading role filter dependent on department (in expanded filters).</td></tr>
            <tr><td><strong>Sort By</strong></td><td>Assigned Courses, Completed %, Completed Count, Cert Count, Completed Cert Count (in expanded filters).</td></tr>
            <tr><td><strong>Engagement Level</strong></td><td>High, Medium, Low (in expanded filters).</td></tr>
            <tr><td><strong>Date Ranges</strong></td><td>Course assigned/completed and Certification assigned/completed date ranges (in expanded filters).</td></tr>
            <tr><td><strong>Assigned Only</strong></td><td>Toggle to show only users with at least 1 assignment (default: ON).</td></tr>
          </tbody>
        </table>
        <p>Active filters display as removable chips.</p>
      </section>

      <section>
        <h2>Table Columns</h2>
        <table>
          <thead>
            <tr>
              <th>Column</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Team Member</strong></td><td>Avatar, name, email.</td></tr>
            <tr><td><strong>Employee ID</strong></td><td>Unique identifier.</td></tr>
            <tr><td><strong>Department &amp; Role</strong></td><td>Primary department and role. Click "+more" for multi-assignment modal.</td></tr>
            <tr><td><strong>Courses Assigned</strong></td><td>Total assigned course count.</td></tr>
            <tr><td><strong>Completed Courses</strong></td><td>Count of completed courses.</td></tr>
            <tr><td><strong>Progress</strong></td><td>Blue progress bar.</td></tr>
            <tr><td><strong>Completion %</strong></td><td>Green progress bar.</td></tr>
            <tr><td><strong>Time Spent (Course)</strong></td><td>Total time in HH:MM:SS format.</td></tr>
            <tr><td><strong>Certs Assigned</strong></td><td>Certification assignment count.</td></tr>
            <tr><td><strong>Certs Completed</strong></td><td>Completed certification count.</td></tr>
            <tr><td><strong>Time Spent (Cert)</strong></td><td>Total cert time in HH:MM:SS.</td></tr>
            <tr><td><strong>Streak</strong></td><td>Learning streak days.</td></tr>
            <tr><td><strong>Engagement</strong></td><td>High/Medium/Low color-coded badge.</td></tr>
            <tr><td><strong>Last Active</strong></td><td>Last activity date.</td></tr>
            <tr><td><strong>Actions</strong></td><td>View Courses and View Certifications buttons.</td></tr>
          </tbody>
        </table>
        <p>Export to Excel or CSV (permission-gated).</p>
      </section>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/91a6d0ad-3974-4973-9cdc-63d1991d6e6b.png" alt="My Team Progress" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: My Team Progress tracking</p></div>

    </div>`,
  },

  "department-progress": {
    title: "Department Progress",
    subtitle: "Department-wide Learning Metrics",
    color: "emerald",
    badge: "Analytics & Reports",
    description:
      "Track department-wide learning metrics including member counts, course/certification progress, and engagement levels. Identify high-performing and struggling departments.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p><strong>Department Learning Progress</strong> provides department-level learning metrics, helping you identify high-performing teams and support struggling departments.</p>
      </div>

      <section>
        <h2>Summary Cards</h2>
        <table>
          <thead>
            <tr>
              <th>Card</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Total Departments</strong></td><td>Count of all departments.</td></tr>
            <tr><td><strong>Average Completion Rate</strong></td><td>Organization-wide average completion %.</td></tr>
            <tr><td><strong>Most Engaged Department</strong></td><td>Name of the top-performing department.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Filters</h2>
        <table>
          <thead>
            <tr>
              <th>Filter</th>
              <th>Options</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Search</strong></td><td>Search by department name.</td></tr>
            <tr><td><strong>Engagement Level</strong></td><td>All, High, Medium, Low.</td></tr>
            <tr><td><strong>Completion Rate Range</strong></td><td>0&ndash;25% (Needs Attention), 26&ndash;50%, 51&ndash;75%, 76&ndash;100% (Excellent).</td></tr>
          </tbody>
        </table>
        <p>Active filters display as removable chips. A Refresh button reloads the data.</p>
      </section>

      <section>
        <h2>Table Columns</h2>
        <table>
          <thead>
            <tr>
              <th>Column</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Department</strong></td><td>Icon, name, and group key.</td></tr>
            <tr><td><strong>Members</strong></td><td>Employee count.</td></tr>
            <tr><td><strong>Courses</strong></td><td>Course count.</td></tr>
            <tr><td><strong>Progress %</strong></td><td>Indigo progress bar.</td></tr>
            <tr><td><strong>Course Status</strong></td><td>C (Completed/green), P (In Progress/blue), N (Not Started/gray) breakdown.</td></tr>
            <tr><td><strong>Completed %</strong></td><td>Green progress bar.</td></tr>
            <tr><td><strong>Certifications</strong></td><td>Certification count.</td></tr>
            <tr><td><strong>Cert Status</strong></td><td>C/P/N breakdown.</td></tr>
            <tr><td><strong>Engagement</strong></td><td>High/Medium/Low color-coded badge.</td></tr>
            <tr><td><strong>Actions</strong></td><td>Navigate to department members view.</td></tr>
          </tbody>
        </table>
        <p>Export to Excel or CSV (permission-gated).</p>
      </section>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/265c2512-7e70-42b6-9621-9b219bba9ba6.png" alt="Department Progress" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Department Progress tracking</p></div>

    </div>`,
  },

  "role-progress": {
    title: "Role Progress",
    subtitle: "Role-wide Learning Metrics",
    color: "emerald",
    badge: "Analytics & Reports",
    description:
      "Track role-wide learning metrics including user counts, course/certification progress, and engagement levels per role. Filter by search, engagement, and completion range.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p><strong>Role Learning Progress</strong> tracks learning metrics per role, helping you identify top-performing roles and those needing support.</p>
      </div>

      <section>
        <h2>Summary Cards</h2>
        <table>
          <thead>
            <tr>
              <th>Card</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Total Roles</strong></td><td>Count of all roles.</td></tr>
            <tr><td><strong>Average Completion Rate</strong></td><td>Organization-wide average completion %.</td></tr>
            <tr><td><strong>Top Performing Role</strong></td><td>Name of the highest-performing role.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Filters</h2>
        <table>
          <thead>
            <tr>
              <th>Filter</th>
              <th>Options</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Search</strong></td><td>Search by role name.</td></tr>
            <tr><td><strong>Engagement Level</strong></td><td>All, High, Medium, Low.</td></tr>
            <tr><td><strong>Completion Rate Range</strong></td><td>0&ndash;25% (Needs Attention), 26&ndash;50% (Below Average), 51&ndash;75% (Average), 76&ndash;100% (Excellent).</td></tr>
          </tbody>
        </table>
        <p>Active filters display as removable chips. A Refresh button reloads the data.</p>
      </section>

      <section>
        <h2>Table Columns</h2>
        <table>
          <thead>
            <tr>
              <th>Column</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Role</strong></td><td>Icon, role name, and role key.</td></tr>
            <tr><td><strong>Users</strong></td><td>User count in role.</td></tr>
            <tr><td><strong>Courses</strong></td><td>Course count.</td></tr>
            <tr><td><strong>Progress %</strong></td><td>Indigo progress bar.</td></tr>
            <tr><td><strong>Course Status</strong></td><td>C (Completed/green), P (In Progress/blue), N (Not Started/gray) breakdown.</td></tr>
            <tr><td><strong>Completed %</strong></td><td>Green progress bar.</td></tr>
            <tr><td><strong>Certifications</strong></td><td>Certification count.</td></tr>
            <tr><td><strong>Cert Status</strong></td><td>C/P/N breakdown.</td></tr>
            <tr><td><strong>Engagement</strong></td><td>High/Medium/Low color-coded badge.</td></tr>
            <tr><td><strong>Actions</strong></td><td>Navigate to role users view.</td></tr>
          </tbody>
        </table>
        <p>Export to Excel or CSV (permission-gated).</p>
      </section>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/f056e2af-2133-4e7d-be86-5682239dd233.png" alt="Role Progress" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Role Progress tracking</p></div>

    </div>`,
  },

  "activity-report": {
    title: "Activity Report",
    subtitle: "System Activity Monitoring",
    color: "emerald",
    badge: "Analytics & Reports",
    description:
      "Comprehensive activity monitoring with event type metrics, time-filtered activity table showing status, user, performer, device info, and IP address. Export to CSV.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p><strong>Activity Reports</strong> provides comprehensive activity monitoring and system usage analytics. View event metrics, filter by time period and status, and drill into individual activity records with device and performer details.</p>
      </div>

      <section>
        <h2>Time Filter</h2>
        <p>All Time, Week, Month, Quarter, Year, or Custom date range with animated active button.</p>
      </section>

      <section>
        <h2>Filters</h2>
        <table>
          <thead>
            <tr>
              <th>Filter</th>
              <th>Options</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Search Activities</strong></td><td>Search by action name.</td></tr>
            <tr><td><strong>Status</strong></td><td>All, Success, Info, Warning, Error.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Metrics Overview</h2>
        <p>Up to 8 event type cards in a grid, each showing event count, event name, percentage of total, and a color-coded icon. Event types include USER_REGISTERED, USER_LOGIN_SUCCESS, PROFILE_UPDATED, DEPARTMENT_CREATED, ROLE_CREATED, COURSE_STARTED, COURSE_COMPLETED, USER_LOGOUT.</p>
      </section>

      <section>
        <h2>Activities Table</h2>
        <table>
          <thead>
            <tr>
              <th>Column</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Status</strong></td><td>Icon + color badge (success/warning/info/error).</td></tr>
            <tr><td><strong>Activity</strong></td><td>Action name, details text, and type label.</td></tr>
            <tr><td><strong>User</strong></td><td>Name, email, user ID.</td></tr>
            <tr><td><strong>Performed By</strong></td><td>"Self" or admin name/email with Shield icon for admin-on-behalf-of actions.</td></tr>
            <tr><td><strong>Device</strong></td><td>Browser, browser version, OS, OS version, device type.</td></tr>
            <tr><td><strong>IP Address</strong></td><td>Client IP address.</td></tr>
            <tr><td><strong>Time</strong></td><td>Relative time ("X days ago") + exact datetime.</td></tr>
          </tbody>
        </table>
        <p>Pagination: 10, 20, 50, or 100 per page. Export to CSV (permission-gated).</p>
      </section>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/7b56becf-dea7-4ddd-98df-98460dbf6009.png" alt="Activity Report" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Activity Report</p></div>

    </div>`,
  },

  "audit-trail-report": {
    title: "Audit Trail Report",
    subtitle: "System Change & User Action Audit Log",
    color: "emerald",
    badge: "Analytics & Reports",
    description:
      "Track and review every system change and user action on the platform. Browse the audit log, filter by event type / user / date range, expand any record to see who performed it, the device used, and a side-by-side JSON diff of old vs. new values. Export filtered records to Excel or CSV.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p><strong>Audit Trail Report</strong> is the system-of-record for every change made on the tenant — user CRUD, role / permission updates, course assignments, notification sends, schedules, and more. Each row collapses to the bare event type + time, and expands to a full forensic view including the actor, device fingerprint, IP address, recipient list, and a field-level diff of what changed. The page is permission-gated by <code>ADMIN_AUDIT_TRAIL_READ</code>; export is additionally gated by <code>ADMIN_AUDIT_TRAIL_WRITE</code>.</p>
      </div>

      <section>
        <h2>Header</h2>
        <p>A blue gradient header surfaces the title, live total record count, and three action buttons. The total count updates with the current filter set, so you can see at a glance how many records match before exporting.</p>
        <table>
          <thead>
            <tr>
              <th>Action</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Filters</strong></td><td>Toggle button that shows / hides the filter panel below the header.</td></tr>
            <tr><td><strong>Excel</strong></td><td>Export the currently-filtered records as an Excel file. Disabled when the page has no permission to write or the result set is empty. Shows a spinner while the export is generated and a toast with the row count on success.</td></tr>
            <tr><td><strong>CSV</strong></td><td>Same as Excel but emits a CSV file. Both formats honor the current filter parameters server-side — never just the visible page.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Audit Log</h2>
        <p>The list is a compact three-column table sorted newest first. Clicking anywhere on a row (or the chevron) expands that row inline to reveal the full record.</p>

        <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/ea3d47ed-42eb-4f64-b55b-4705af0aa87a.png" alt="Audit Trail list — default view, recent records only" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Audit trail list — default view (recent hot records only, archived months excluded)</p></div>

        <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/269e92ba-ecb8-45ee-a80d-d35aa0ee6e4a.png" alt="Audit Trail list — Include Archived enabled" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Audit trail list with <strong>Include Archived</strong> on — older archived months merged into the same results</p></div>

        <h3>Columns</h3>
        <table>
          <thead>
            <tr>
              <th>Column</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>S.NO</strong></td><td>1-based row number across the full result set (continues across pages).</td></tr>
            <tr><td><strong>Event Type</strong></td><td>Human-readable event name. Raw codes like <code>USER_DELETED</code> are formatted to "User Deleted". A small override map renames domain-specific codes — for example <code>GROUP_CREATED</code> shows as <strong>Department Created</strong> (the admin UI surfaces Groups as Departments), and <code>ROLE_PERMISSION_ASSIGNED</code> shows as <strong>Permission Assigned To Role</strong>.</td></tr>
            <tr><td><strong>Event Date &amp; Time</strong></td><td>Timestamp when the event was recorded, formatted in the admin's locale.</td></tr>
            <tr><td><strong>Action</strong></td><td>Chevron button that expands / collapses the detail view for that row.</td></tr>
          </tbody>
        </table>
        <p>Pagination supports 10 / 20 / 50 / 100 per page. Changing the page size resets to page 1.</p>
      </section>

      <section>
        <h2>Filters</h2>
        <p>Click the <strong>Filters</strong> button in the header to reveal the filter panel. Click <strong>Apply Filters</strong> to refresh the list, or <strong>Clear All</strong> to reset every field to its default.</p>
        <table>
          <thead>
            <tr>
              <th>Filter</th>
              <th>Behavior</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Event type</strong></td><td>Single-select dropdown populated from the backend's event registry. Options carry their friendly labels (already de-cased and override-mapped). Clearable.</td></tr>
            <tr><td><strong>User</strong></td><td>Free-text search by username or email. Debounced (300 ms) and applied automatically — pressing Enter triggers an immediate search.</td></tr>
            <tr><td><strong>Start date</strong></td><td>Calendar picker. Cannot be a future date. If a previously chosen End date is now earlier, End date is bumped to match.</td></tr>
            <tr><td><strong>End date</strong></td><td>Calendar picker bounded to <em>Start date ≤ End date ≤ Today</em>. Picking a future date silently clamps to today.</td></tr>
          </tbody>
        </table>
        <div class="sz-callout sz-callout-tip">
          <p>Filter changes update the URL-less query params, the total record count in the header, and the export payload at the same time — what you see is what you'll export.</p>
        </div>
      </section>

      <section>
        <h2>Archiving &amp; Historical Data (Hot / Cold Storage)</h2>
        <p>Audit records accumulate forever, so to keep the live database fast the platform moves older records out of MongoDB into compressed files in the tenant's own S3 bucket. Records aged past the retention window (30 days) are archived one complete month at a time — nothing is deleted or lost, it is just relocated so the hot collection stays small and every query stays quick.</p>

        <div class="sz-callout sz-callout-info">
          <p>Archival runs automatically once a day at <strong>2:00 AM UTC</strong> (off-peak), per tenant. For each old month the system writes two gzip files to S3 — a small <strong>index file</strong> holding the light, searchable fields and a full <strong>data file</strong> holding the complete records — then records a single catalog row in the archive manifest and removes that month from the live collection. Files are always uploaded <em>before</em> anything is deleted, so a failed upload can never lose data, and re-running a month simply overwrites it (idempotent). Compression shrinks the stored size roughly 10×.</p>
        </div>

        <h3>Include Archived toggle</h3>
        <p>By default the list shows only recent (hot) records, so the page loads instantly. Turn on <strong>Include Archived</strong> — a Datadog-style "Historical Index" opt-in — to also search the archived months. Filtering, searching, and sorting behave <em>exactly</em> the same on archived data: the system reads only the tiny index files (never the big data files) to find matches, merges them with the live results, and paginates the combined set. The full data file is opened only when you expand a single archived record to view its detail, and even then it streams and stops at the matching row. Setting any date range also pulls in the overlapping archived months automatically.</p>
        <table>
          <thead>
            <tr>
              <th>Include Archived</th>
              <th>What is searched</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Off (default)</strong></td><td>Only the recent records still in the live database. Fastest — no S3 access.</td></tr>
            <tr><td><strong>On</strong></td><td>Live records <em>plus</em> the archived months in S3, merged and sorted as one list. Distinct-value pruning in the manifest skips months that can't match the chosen event type before any file is downloaded.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Expanded Record View</h2>
        <p>Expanding a row reveals every detail the SDK captured for that event. The detail block is built dynamically — only fields that have a value for this event are rendered, so each event type shows just what's relevant.</p>

        <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/1d943650-f957-4c07-b84c-ac2b6ceea550.png" alt="Audit Trail Expanded Detail with JSON Diff" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Expanded record with field-level JSON diff</p></div>

        <h3>Detail rows</h3>
        <table>
          <thead>
            <tr>
              <th>Row</th>
              <th>When it appears</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Note</strong></td><td>The event description, enriched with the resource name when available. For assignment events it reads "Assigned [Course / Cert / Path name] to [User]" — multi-recipient assignments collapse to "Assigned … to N users" with a <strong>+N more</strong> / <strong>Show less</strong> toggle.</td></tr>
            <tr><td><strong>Failure Reason</strong></td><td>Shown in red for any <code>*_FAILED</code> event. Pulls from <code>failure_reason</code>, <code>reason</code>, <code>error</code>, or <code>error_description</code> — whichever the SDK populated.</td></tr>
            <tr><td><strong>Created By / Updated By / Deleted By / Restored By / Performed By</strong></td><td>The actor that triggered the event. The label adapts to the event type (CREATE / UPDATE / DELETE / RESTORE) so the row reads naturally. Always sourced from <code>action_by_info</code> so that fields like <code>email</code> on a <code>USER_CREATED</code> event (which actually hold the <em>new user's</em> email) never get mis-labelled as the actor.</td></tr>
            <tr><td><strong>Browser / Device / Browser IP</strong></td><td>Device fingerprint from the original request — browser name, OS, and client IP.</td></tr>
            <tr><td><strong>Sent By / Sent To</strong></td><td>For send events (notifications, reminders, invitations). System-generated sends (<code>*_AUTO_SENT</code>) show <strong>Sent By: System</strong>; admin-triggered sends show the admin's name + email.</td></tr>
            <tr><td><strong>Scheduled By / Scheduled To</strong></td><td>Same as above but used for events that contain <code>SCHEDULED</code> — makes it obvious the action is queued for later, not delivered yet.</td></tr>
            <tr><td><strong>Recipients</strong></td><td>Green pill list for multi-recipient events (group / role assignments). Shows the first 3 recipients with a <strong>+N more</strong> button; expanded view shows all and offers <strong>Show less</strong>. For events that only carry a count, the row collapses to "<em>N users</em>".</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>JSON Diff (Old vs. New)</h2>
        <p>When the event carries both <code>old_data</code> and <code>new_data</code>, a side-by-side JSON diff renders below the detail rows. Built specifically to answer the question "which fields were updated, and what changed?" — internal noise is hidden so the eye lands on the actual edit.</p>
        <table>
          <thead>
            <tr>
              <th>Behavior</th>
              <th>Why</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Only changed fields rendered</strong></td><td>Keys whose old and new values are identical (by JSON-serialized equality) are filtered out, so a 30-field document with a one-field edit doesn't bury the change.</td></tr>
            <tr><td><strong>Internal fields hidden</strong></td><td><code>_id</code>, <code>__v</code>, <code>createdAt</code>, <code>updatedAt</code>, <code>created_at</code>, <code>updated_at</code>, <code>created_by</code>, <code>updated_by</code>, <code>is_deleted</code>, <code>tenant_key</code>, <code>password_hash</code>, <code>access_token</code>, <code>refresh_token</code>, and <code>invitation_token</code> are never shown — they're Mongo internals, always-changing timestamps, or credentials.</td></tr>
            <tr><td><strong>Word-level highlight</strong></td><td>For changed values, an LCS-based diff highlights only the tokens that differ — removed words get a red background on the left, added words get a green background on the right. Common words stay unstyled so the diff jumps out instead of painting the whole value.</td></tr>
            <tr><td><strong>Create operation</strong></td><td>When <code>old_data</code> is empty (a CREATE event), the diff collapses to a single green <em>"Created data:"</em> panel listing the new document's non-internal fields.</td></tr>
            <tr><td><strong>No meaningful changes</strong></td><td>If every change was on a hidden internal field, the panel shows "No field-level changes detected" instead of rendering an empty diff.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Export</h2>
        <p>Both export buttons hit a server-side export endpoint with the current filter set, so the file always reflects the filtered view (not just the visible page). The button shows a spinner while the export runs and emits a toast with the exported row count when it completes. Empty result sets surface a friendly toast instead of producing a 0-row file.</p>
        <p>Export is permission-gated — admins with read-only access see the buttons disabled with an explanatory tooltip; only roles holding <code>ADMIN_AUDIT_TRAIL_WRITE</code> can download.</p>
      </section>

      <section>
        <h2>Common use cases</h2>
        <div class="sz-use-cases">
          <div class="sz-use-case">
            <div class="sz-use-case-title">"Who changed this user's role last week?"</div>
            <div class="sz-use-case-flow">
              Open Filters → set Event type to <strong>User Updated</strong> (or <strong>Permission Assigned To Role</strong>) → set the date range to the past week → search for the user's email. Expand the row to see <strong>Updated By</strong>, the old / new role in the JSON diff, and the browser + IP the change came from.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Investigate a failed notification send</div>
            <div class="sz-use-case-flow">
              Filter Event type to a <code>*_FAILED</code> event (or search the recipient's email). Expand the row — the red <strong>Failure Reason</strong> line plus the device / IP / actor info gives you everything you need to root-cause without leaving the page.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Export an audit packet for compliance</div>
            <div class="sz-use-case-flow">
              Set the start / end date range that the auditor requested, optionally narrow the event type, and click <strong>Excel</strong>. The exported file contains the full filtered set — not just the visible page — so a single click captures everything in scope.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Confirm a bulk assignment landed</div>
            <div class="sz-use-case-flow">
              After running a bulk course assignment, filter Event type to the assignment event and search by the admin's email. Expand the row — the <strong>Recipients</strong> pill list (with +N more) confirms exactly which users were targeted, so you can verify against the intended audience.
            </div>
          </div>
        </div>
      </section>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Tip</div>
        <p>Audit records are immutable — every change is appended, never overwritten. If a row says a field went from A → B at 14:32, that's the on-the-wire truth; later corrections show up as their own rows, never a backfilled edit of the original.</p>
      </div>

    </div>`,
  },

  "email-logs": {
    title: "Email Logs",
    subtitle: "Email Communication Tracking",
    color: "emerald",
    badge: "Analytics & Reports",
    description:
      "Monitor and track email communications with a master-detail split panel layout. Filter by status tabs, date range, email type, and search. Retry failed emails directly from the detail view.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p><strong>Email Logs</strong> provides a master-detail interface for monitoring all platform email communications. Browse emails in the left panel, view full details in the right panel, and retry failed sends.</p>
      </div>

      <section>
        <h2>Layout</h2>
        <p>A full-height split panel layout:</p>
        <table>
          <thead>
            <tr>
              <th>Panel</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Left (480px)</strong></td>
              <td>Scrollable email log list with pagination fixed at the bottom.</td>
            </tr>
            <tr>
              <td><strong>Right (flex)</strong></td>
              <td>Email detail view. Shows a placeholder when nothing is selected. On mobile, the list hides when a detail is open with a "Back to list" button.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Status Tabs</h2>
        <p>Tabs with live counts from the metrics API:</p>
        <table>
          <thead>
            <tr>
              <th>Tab</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>All</strong></td><td>All emails.</td></tr>
            <tr><td><strong>Sent</strong></td><td>Emails successfully sent.</td></tr>
            <tr><td><strong>Delivered</strong></td><td>Emails confirmed delivered.</td></tr>
            <tr><td><strong>Failed</strong></td><td>Emails that failed to send.</td></tr>
            <tr><td><strong>Pending</strong></td><td>Emails queued for sending.</td></tr>
            <tr><td><strong>Bounced</strong></td><td>Emails that bounced back.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Filters</h2>
        <table>
          <thead>
            <tr>
              <th>Filter</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Date Range</strong></td><td>From and To date pickers with clear button.</td></tr>
            <tr><td><strong>Email Type</strong></td><td>Dropdown populated from API (clearable).</td></tr>
            <tr><td><strong>Search</strong></td><td>Search by email address or name.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Email Detail</h2>
        <p>Selecting an email in the list shows its full details in the right panel, including sender, recipient, type, status, and content. For failed emails, a <strong>Retry</strong> button re-attempts sending.</p>
      </section>

      <section>
        <h2>Archiving &amp; Historical Data (Hot / Cold Storage)</h2>
        <p>Email logs grow quickly, so older entries are moved out of the live database into compressed files in the tenant's own S3 bucket to keep the page fast. Entries aged past the retention window (30 days) are archived one complete month at a time; nothing is lost, it is only relocated.</p>

        <div class="sz-callout sz-callout-info">
          <p>Archival runs automatically once a day at <strong>2:00 AM UTC</strong>, per tenant. Each old month is written to S3 as a small searchable <strong>index file</strong> plus a full <strong>data file</strong>, catalogued with one manifest row, and then removed from the live collection — files uploaded before any deletion, so data is never at risk, and roughly 10× smaller once compressed.</p>
        </div>

        <div class="sz-callout sz-callout-tip">
          <div class="sz-callout-label">Only settled emails are archived</div>
          <p>An email is archived only once it has reached a terminal status (Sent / Delivered / Failed / Bounced) with no pending retry. Anything still <strong>Pending</strong> or awaiting a retry stays in the live list so it remains actionable — you will never lose sight of an in-flight or retryable email because of archiving.</p>
        </div>

        <h3>Include Archived toggle</h3>
        <p>By default the list shows only recent (hot) emails, so it loads instantly. Turn on <strong>Include Archived</strong> — a Datadog-style "Historical Index" opt-in — to also search the archived months. Status tabs, date range, type, and search all work identically on archived data: only the tiny index files are read to find matches and merge them with the live results; the full data file is opened only when you open one archived email's detail.</p>

        <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/9ef0a2c7-b572-4590-a1f9-922a74e70d47.png" alt="Email Logs — default view, recent records only" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Email Logs — default view (recent hot records only, archived months excluded)</p></div>

        <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/4d507795-8bce-4bc4-96dd-35d55a794c33.png" alt="Email Logs — Include Archived enabled" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Email Logs with <strong>Include Archived</strong> on — older archived months merged into the same list</p></div>
      </section>

      <section>
        <h2>Common use cases</h2>
        <div class="sz-use-cases">
          <div class="sz-use-case">
            <div class="sz-use-case-title">Troubleshoot failed emails</div>
            <div class="sz-use-case-flow">
              Click the Failed tab to see all failed emails. Select one to view the error details. Click Retry to re-attempt sending.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Audit email communications</div>
            <div class="sz-use-case-flow">
              Set the date range and email type to review all notifications sent during a specific period. Check delivery status to ensure learners received important communications.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Look up an old email from months ago</div>
            <div class="sz-use-case-flow">
              Turn on <strong>Include Archived</strong> (or set a date range covering the period), then search by recipient or type. Archived months are pulled from S3 and merged into the list so historical sends surface right alongside recent ones.
            </div>
          </div>
        </div>
      </section>

    </div>`,
  },

  "training-status-report": {
    title: "Training Status Report",
    subtitle: "Complete Learner Tracking & Overdue Analysis",
    color: "emerald",
    badge: "Analytics & Reports",
    description:
      "End-to-end training status for every learner across courses, certifications, and learning paths. Clickable status and urgency cards, urgency and tracking charts, department/item breakdowns, a 20-column records table with sorting and search, and a 5-sheet Excel export.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p><strong>Training Status Report</strong> gives a single, comprehensive view of every learner's training — all statuses, all deadlines, all urgency levels. Use the clickable status/urgency cards to drill into the records table, and export the full dataset as a multi-sheet Excel workbook for offline review or stakeholder reporting.</p>
      </div>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/f14f4581-19e0-4c3f-bf14-467275c35aa1.png" alt="Training Status Report" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Training Status Report</p></div>

      <section>
        <h2>Report Type Tabs</h2>
        <p>Tabs in the gradient header switch the report scope. The summary, cards, charts, table, and export all refresh for the selected type.</p>
        <table>
          <thead>
            <tr>
              <th>Tab</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>All</strong></td><td>Combined view across courses, certifications, and learning paths.</td></tr>
            <tr><td><strong>Courses</strong></td><td>Course assignments only.</td></tr>
            <tr><td><strong>Certifications</strong></td><td>Certification assignments only.</td></tr>
            <tr><td><strong>Learning Paths</strong></td><td>Learning path assignments only.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Status Cards (clickable)</h2>
        <p>Four cards at the top of the dashboard act as one-click filters for the records table. Clicking a card activates it (ring indicator) and narrows the table. Click <strong>Clear card filter</strong> or the same card again to reset.</p>
        <table>
          <thead>
            <tr>
              <th>Card</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Completed</strong></td><td>Assignments marked as completed.</td></tr>
            <tr><td><strong>Not Started</strong></td><td>Assignments with no progress yet.</td></tr>
            <tr><td><strong>In Progress</strong></td><td>Assignments currently underway.</td></tr>
            <tr><td><strong>No Deadline</strong></td><td>Assignments without a due date.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Urgency Cards (clickable)</h2>
        <p>Four urgency cards (overdue-only) let you filter the table by how far past deadline the assignment is:</p>
        <table>
          <thead>
            <tr>
              <th>Card</th>
              <th>Criteria</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Critical</strong></td><td>30+ days overdue.</td></tr>
            <tr><td><strong>High</strong></td><td>14&ndash;29 days overdue.</td></tr>
            <tr><td><strong>Medium</strong></td><td>7&ndash;13 days overdue.</td></tr>
            <tr><td><strong>Low</strong></td><td>1&ndash;6 days overdue.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Key Metrics</h2>
        <p>Six summary tiles give an at-a-glance snapshot:</p>
        <table>
          <thead>
            <tr>
              <th>Metric</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Total Assignments</strong></td><td>Every learner-item assignment in scope.</td></tr>
            <tr><td><strong>Unique Learners</strong></td><td>Distinct users with at least one assignment.</td></tr>
            <tr><td><strong>Unique Items</strong></td><td>Distinct courses, certifications, and learning paths assigned.</td></tr>
            <tr><td><strong>Completion Rate</strong></td><td>Percentage of assignments completed.</td></tr>
            <tr><td><strong>Mandatory Overdue</strong></td><td>Count of overdue mandatory assignments — usually the highest-priority number on the page.</td></tr>
            <tr><td><strong>Avg. Progress</strong></td><td>Mean progress percentage across all assignments.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Charts &amp; Breakdowns</h2>
        <table>
          <thead>
            <tr>
              <th>Chart</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Urgency Breakdown</strong></td><td>Bar chart of overdue counts by urgency level (Critical / High / Medium / Low).</td></tr>
            <tr><td><strong>Tracking Status Distribution</strong></td><td>Pie chart of Not Started / In Progress / Completed counts.</td></tr>
            <tr><td><strong>By Department</strong></td><td>Horizontal bar chart of top 10 departments by assignment volume.</td></tr>
            <tr><td><strong>By Item</strong></td><td>Horizontal bar chart of top 10 items (courses/certs/paths) by assignment volume.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Filter Bar</h2>
        <table>
          <thead>
            <tr>
              <th>Filter</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Search</strong></td><td>Debounced search by learner name, email, employee ID, item title, or department.</td></tr>
            <tr><td><strong>Mandatory Only</strong></td><td>Restricts the table to mandatory assignments.</td></tr>
            <tr><td><strong>Page Size</strong></td><td>10, 20, 50, or 100 rows per page.</td></tr>
            <tr><td><strong>Clear Card Filter</strong></td><td>Visible only while a status/urgency card is active — resets card-driven filters.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Records Table</h2>
        <p>The main records table shows every assignment matching the active filters. Sticky header, horizontal scroll, and click-to-sort on key columns.</p>
        <table>
          <thead>
            <tr>
              <th>Column</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Learner</strong></td><td>Name and email (sortable by name).</td></tr>
            <tr><td><strong>Emp ID</strong></td><td>Employee identifier.</td></tr>
            <tr><td><strong>Department</strong></td><td>Department name with a link icon to open the full value in a modal.</td></tr>
            <tr><td><strong>Reporting Manager / HRBP / BU Head</strong></td><td>Organizational hierarchy — email shown on hover.</td></tr>
            <tr><td><strong>Item</strong></td><td>Course / certification / learning path title with modal for full title.</td></tr>
            <tr><td><strong>Category</strong></td><td>Category name of the item.</td></tr>
            <tr><td><strong>Type</strong></td><td>Mandatory (red) or Optional (green) tag.</td></tr>
            <tr><td><strong>Status</strong></td><td>Tracking status tag: Completed / In Progress / Not Started.</td></tr>
            <tr><td><strong>Progress</strong></td><td>Purple progress bar plus percentage (sortable).</td></tr>
            <tr><td><strong>Assigned At</strong></td><td>Date assigned.</td></tr>
            <tr><td><strong>Deadline</strong></td><td>Due date or "—" if none (sortable).</td></tr>
            <tr><td><strong>Days Over</strong></td><td>Days past deadline, bold red when &gt; 0 (sortable).</td></tr>
            <tr><td><strong>Urgency</strong></td><td>Color-coded urgency badge for overdue rows.</td></tr>
            <tr><td><strong>Completed At</strong></td><td>Completion date (sortable).</td></tr>
            <tr><td><strong>Last Accessed At</strong></td><td>Last activity date or "Never".</td></tr>
            <tr><td><strong>Time Spent</strong></td><td>Total time in <code>HH:MM:SS</code>.</td></tr>
            <tr><td><strong>Reminders</strong></td><td>Count of reminders sent.</td></tr>
          </tbody>
        </table>
        <p>Pagination controls appear at the bottom when results exceed the selected page size.</p>
      </section>

      <section>
        <h2>Excel Export</h2>
        <p>The <strong>Export Excel</strong> button in the header downloads the full dataset (not just the current page) as a styled <code>.xlsx</code> workbook. File name encodes the active filters and date. The export is permission-gated (write permission required) and contains <strong>5 sheets</strong>:</p>
        <table>
          <thead>
            <tr>
              <th>Sheet</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Summary</strong></td><td>Report metadata, overview, timeline breakdown, tracking status, and urgency breakdown.</td></tr>
            <tr><td><strong>Learner Records</strong></td><td>Every record matching the filters — 25 columns with frozen header, auto-filter, and urgency/mandatory cell styling.</td></tr>
            <tr><td><strong>Overdue Report</strong></td><td>Overdue-only subset (red theme) for urgent follow-up.</td></tr>
            <tr><td><strong>By Department</strong></td><td>Department &rarr; count aggregation.</td></tr>
            <tr><td><strong>By Item</strong></td><td>Item title, type, and count aggregation.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Common use cases</h2>
        <div class="sz-use-cases">
          <div class="sz-use-case">
            <div class="sz-use-case-title">Escalate critical overdue training</div>
            <div class="sz-use-case-flow">
              Click the <strong>Critical</strong> urgency card to filter the table to learners 30+ days past deadline. Use search to narrow by department or manager. Export to Excel and share the Overdue Report sheet with line managers.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Compliance audit for mandatory training</div>
            <div class="sz-use-case-flow">
              Toggle <strong>Mandatory only</strong>, set the tab to <strong>All</strong>, and review the Mandatory Overdue metric. Drill into the Not Started card to see learners who haven't begun required training.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Department performance review</div>
            <div class="sz-use-case-flow">
              Review the By Department chart to identify teams with the highest assignment load. Search by department in the filter bar to see learner-level status, then export for the department head.
            </div>
          </div>
        </div>
      </section>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Tip</div>
        <p>Status and urgency cards are mutually exclusive — clicking a new card replaces the active filter. Combine a card filter with the search box to quickly find a specific learner within a high-urgency cohort. The summary numbers stay constant regardless of table filters, so you always see the true totals for the selected tab.</p>
      </div>

    </div>`,
  },

  "tenant-reports": {
    title: "Tenant Reports",
    subtitle: "Per-Tenant Usage Reports",
    color: "emerald",
    badge: "Analytics & Reports",
    description:
      "Generate, browse, and download a self-contained usage report for any single tenant. Pick a tenant and a rolling time window (or a custom date range), generate an interactive HTML report with engagement score, all-time totals, in-period activity, and charts, then View it in the browser or Download it. Management-tenant only.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p><strong>Tenant Reports</strong> lets the management team produce a comprehensive usage report for one tenant at a time. Choose a tenant and a period, click <strong>Generate Report</strong>, and the platform builds a self-contained interactive report (metric cards + charts) and stores it. Every generated report is listed in a history table where it can be viewed in the browser or downloaded.</p>
      </div>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Management-tenant only</div>
        <p>This page is <strong>only visible and accessible from the management tenant</strong>. On any other tenant host the menu item is hidden, the route redirects to <em>Access Denied</em>, and the backend independently rejects every request that isn't from the management context — so tenant admins can never see another tenant's report.</p>
      </div>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/b563b923-63ef-406e-b3f4-e50b346a66c4.png" alt="Tenant Reports list" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Tenant Reports — generate panel and generated-reports history</p></div>

      <section>
        <h2>Header</h2>
        <p>A purple gradient header shows the page title and two live counters:</p>
        <table>
          <thead>
            <tr>
              <th>Counter</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Total Reports</strong></td><td>Number of reports generated so far (matches the history table's total).</td></tr>
            <tr><td><strong>Active Tenants</strong></td><td>Number of active tenants available to report on.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Generate a Report</h2>
        <p>The generate panel controls which tenant and period the report covers:</p>
        <table>
          <thead>
            <tr>
              <th>Control</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Tenant</strong></td><td>Searchable dropdown of active tenants. Each option shows the tenant name and key, e.g. <code>Demo (skillzen_demo)</code>, to disambiguate similarly-named tenants. Required.</td></tr>
            <tr><td><strong>Period</strong></td><td>Rolling time window for the report (see the table below).</td></tr>
            <tr><td><strong>From / To</strong></td><td>Date pickers that appear only when <strong>Custom range</strong> is selected. Future dates are disabled, and <em>To</em> can't precede <em>From</em>.</td></tr>
            <tr><td><strong>Generate Report</strong></td><td>Builds and stores the report. Disabled until a tenant is chosen; shows a spinner while generating.</td></tr>
          </tbody>
        </table>
        <h3>Period options</h3>
        <p>Periods are <strong>rolling windows relative to today</strong>, not calendar periods — "Last 30 Days" means the previous 30 days, not the current calendar month.</p>
        <table>
          <thead>
            <tr>
              <th>Label</th>
              <th>Window</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Last 7 Days</strong></td><td>Rolling 7-day window.</td></tr>
            <tr><td><strong>Last 30 Days</strong></td><td>Rolling 30-day window.</td></tr>
            <tr><td><strong>Last 90 Days</strong></td><td>Rolling 90-day window.</td></tr>
            <tr><td><strong>Last 1 Year</strong></td><td>Rolling 365-day window.</td></tr>
            <tr><td><strong>Custom range</strong></td><td>Exact From/To dates you choose.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Generated Reports Table</h2>
        <p>A history of every report, newest first. Empty until you generate your first one.</p>
        <table>
          <thead>
            <tr>
              <th>Column</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Tenant</strong></td><td>Tenant display name with its key underneath.</td></tr>
            <tr><td><strong>Generated By</strong></td><td>Name (and email) of the admin who generated it, resolved server-side; "System User" if unattributed.</td></tr>
            <tr><td><strong>Status</strong></td><td>Color-coded pill: <strong>Success</strong> (green), <strong>Failed</strong> (red), <strong>Processing</strong> (amber, spinning), or <strong>Pending</strong> (gray).</td></tr>
            <tr><td><strong>Generated At</strong></td><td>Date and time the report was created (<code>dd/MM/yyyy HH:mm</code>).</td></tr>
            <tr><td><strong>Actions</strong></td><td>For successful reports: <strong>View</strong> and <strong>Download</strong>. Failed reports show "Generation failed" (hover for the error); pending/processing show a dash.</td></tr>
          </tbody>
        </table>
        <p>Paginated with 10, 20, or 50 rows per page.</p>
        <h3>Actions</h3>
        <table>
          <thead>
            <tr>
              <th>Action</th>
              <th>Behavior</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>View</strong></td><td>Opens the report in a new browser tab (interactive HTML with live charts).</td></tr>
            <tr><td><strong>Download</strong></td><td>Saves the report file locally. The file is streamed through the authenticated backend so the download is reliable, and is named <code>&lt;tenant&gt;-usage-report-&lt;date&gt;.html</code>.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Inside the Report</h2>
        <p>Each report is a standalone, styled HTML document that opens in any browser — no login required to read the file itself. It is scoped to the tenant and period you selected.</p>

        <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/5b1ebb4f-30df-4193-b63d-c2df566b642e.png" alt="Generated tenant usage report" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Generated tenant usage report</p></div>

        <table>
          <thead>
            <tr>
              <th>Section</th>
              <th>What it shows</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Header</strong></td><td>Tenant name, the selected date range (with day count), and when the report was generated.</td></tr>
            <tr><td><strong>Engagement Score</strong></td><td>A 0&ndash;100 composite score with a label — <strong>Excellent</strong> (80+), <strong>Good</strong> (60+), <strong>Moderate</strong> (40+), <strong>Low</strong> (20+), or <strong>Minimal</strong> — based on user activation, course completions, AI usage, certifications, and community activity.</td></tr>
            <tr><td><strong>All-Time Totals</strong></td><td>Since-inception metric cards: users (active/inactive), courses by stage, content volume, course completions, assignments, certifications issued, learning paths, assessments, AI requests/tutor chats/course generations, knowledge stores, reviews, emails sent, and database size.</td></tr>
            <tr><td><strong>Activity During Period</strong></td><td>Cards counting only what happened in the selected window: DAU/MAU, users logged in, new users, courses created, completions, new assignments, certificates issued, AI usage, emails, tickets, and invitations.</td></tr>
            <tr><td><strong>Charts</strong></td><td>Assignment status (period), Users by group and by role (all-time), AI usage by feature (period), and course generation status (period).</td></tr>
            <tr><td><strong>Top Courses by Completion</strong></td><td>The five most-completed courses within the period.</td></tr>
            <tr><td><strong>Gamification &amp; Engagement</strong></td><td>Learning streaks, badges configured, forum posts, trending entries, webhooks, invitations, and notifications.</td></tr>
            <tr><td><strong>Footer</strong></td><td>Report scope and a <em>Confidential</em> marker.</td></tr>
          </tbody>
        </table>
        <div class="sz-callout sz-callout-tip">
          <p>The report surfaces <strong>usage counts only — never cost or dollar figures</strong>, so it's safe to share with the tenant/customer directly.</p>
        </div>
      </section>

      <section>
        <h2>Common use cases</h2>
        <div class="sz-use-cases">
          <div class="sz-use-case">
            <div class="sz-use-case-title">Quarterly review with a customer</div>
            <div class="sz-use-case-flow">
              Select the tenant, set the period to <strong>Last 90 Days</strong>, and generate. Open the report with <strong>View</strong> to walk through the engagement score and in-period activity on a call, or <strong>Download</strong> the HTML file to share with the customer.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Health check on a quiet tenant</div>
            <div class="sz-use-case-flow">
              Generate a <strong>Last 30 Days</strong> report and check the engagement banner and DAU/MAU cards. A "Minimal" or "Low" label plus near-zero period activity flags a tenant that may need onboarding support or re-engagement.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Point-in-time snapshot for a specific window</div>
            <div class="sz-use-case-flow">
              Choose <strong>Custom range</strong>, pick the exact From/To dates (for example a campaign or pilot period), and generate. The report's "Activity During Period" section reflects only that window.
            </div>
          </div>
        </div>
      </section>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Tip</div>
        <p>All-Time Totals reflect the tenant's entire history regardless of the period you pick — only the "Activity During Period" section and the period charts are scoped to your selected window. Generate reports for the same window across tenants to compare adoption side by side.</p>
      </div>

    </div>`,
  },

  "ai-usage": {
    title: "AI Usage",
    subtitle: "Your Organization's AI Consumption",
    color: "emerald",
    badge: "Analytics & Reports",
    description:
      "See how your own organization is consuming AI — credits, tokens, and requests across every feature and model. Pick a period to drive the summary cards, usage trend, and records table; drill into any record for its per-call breakdown; and export the filtered records to CSV. Older periods are served seamlessly from the archive.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p><strong>AI Usage</strong> is the tenant-scoped view of AI consumption — every tenant sees <strong>only its own</strong> usage. It answers "how much AI are we using, on what feature, on which model, by whom, and how is that trending?" Figures are shown in <strong>credits</strong>, with tokens and request counts alongside. The page is gated by the <code>admin_ai_usage_read</code> / <code>admin_ai_usage_write</code> role permission.</p>
      </div>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Not the same as AI Cost Tracking</div>
        <p><strong>AI Usage</strong> (this page) is tenant-scoped — each tenant sees its own consumption. <strong>AI Cost Tracking</strong> is a separate, management-tenant-only dashboard that compares spend <em>across all tenants</em>. Same underlying data, different scope and audience.</p>
      </div>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/60fdccf5-c84f-42d4-b91d-609218b5052e.png" alt="AI Usage dashboard" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: AI Usage — summary cards, usage trend, and records table</p></div>

      <section>
        <h2>Header</h2>
        <p>A violet gradient header shows the title and a single action:</p>
        <table>
          <thead>
            <tr>
              <th>Action</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Export CSV</strong></td><td>Exports the currently-filtered records (up to 1,000 rows) as a CSV file. Disabled while an export is running or when there are no records. The file is UTF-8 with a BOM so Excel decodes non-ASCII (e.g. Tamil) names correctly.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Period (global control)</h2>
        <p>The <strong>Period</strong> control is the master filter — it drives the summary cards, the trend chart, <em>and</em> the records table at once. Presets: Last 7 days, Last 30 days (default), Last 90 days, Last 6 months, Last 1 year, All time, or a Custom From/To range. Future dates are disabled.</p>
        <div class="sz-callout sz-callout-tip">
          <p>The period also decides how much history is pulled from the archive — pick an older range and the page automatically includes the archived months.</p>
        </div>
      </section>

      <section>
        <h2>Summary Cards</h2>
        <p>Six tiles summarize the selected period:</p>
        <table>
          <thead>
            <tr>
              <th>Card</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Total Credits</strong></td><td>Total credits consumed in the period.</td></tr>
            <tr><td><strong>Total Tokens</strong></td><td>All input + output tokens across every call.</td></tr>
            <tr><td><strong>Total Requests</strong></td><td>Number of AI requests made.</td></tr>
            <tr><td><strong>Highest Usage Feature</strong></td><td>The feature that consumed the most in the period.</td></tr>
            <tr><td><strong>Highest Usage User</strong></td><td>The user who consumed the most credits in the period — i.e. the highest-spend user, not merely the most frequent one — shown as name + email.</td></tr>
            <tr><td><strong>Input / Output Tokens</strong></td><td>The prompt-vs-completion token split.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Usage Trend</h2>
        <p>A line chart of usage over the period, with a metric switch: <strong>Credits</strong>, <strong>Tokens</strong>, or <strong>Requests</strong>. The Tokens view breaks out Input, Output, and Total lines. Granularity (day / week / month) is chosen automatically from the length of the selected range, and every bucket in the range is plotted (gaps show as zero) so the timeline is continuous.</p>
      </section>

      <section>
        <h2>Records Table</h2>
        <p>A per-record table under the trend, with its own drill-down filters layered on top of the global Period:</p>
        <table>
          <thead>
            <tr>
              <th>Filter</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Feature</strong></td><td>Narrow to one feature (Text Generation, Image, Embeddings, Speech, etc.).</td></tr>
            <tr><td><strong>Model</strong></td><td>Any model from AI Model Settings, unioned with models already seen in this tenant's usage.</td></tr>
            <tr><td><strong>Status</strong></td><td>All, Success, or Failed.</td></tr>
            <tr><td><strong>Search user / email</strong></td><td>Free-text on user name or email (debounced).</td></tr>
          </tbody>
        </table>
        <p>Columns: User, Feature, Model, Tokens, Credits/Cost, Status, Date, and a Detail action. The Credits and Date columns are sortable. Paginated 20 per page. <strong>Clear</strong> resets the table-only filters (the Period is left untouched).</p>
      </section>

      <section>
        <h2>Detail Drawer</h2>
        <p>Clicking the eye icon on a row opens a drawer with that record's full breakdown — user, model, date, total credits, the input/output/total token split, and a <strong>per-call breakdown</strong> table (stage, service, in/out tokens, credits, and time for each underlying call). Failed calls are highlighted.</p>
      </section>

      <section>
        <h2>Archiving &amp; Historical Data (Hot / Cold Storage)</h2>
        <p>Usage records accumulate on every AI call, so older raw records are moved out of MongoDB into compressed files in the tenant's own S3 bucket to keep the live database fast. Records aged past the retention window (30 days) are archived one complete month at a time — nothing is lost, and your totals stay correct because a per-month <strong>snapshot</strong> is computed before the raw rows are removed.</p>

        <div class="sz-callout sz-callout-info">
          <p>Archival runs automatically once a day at <strong>2:00 AM UTC</strong>, per tenant. For each old month the system stores a light <strong>index file</strong> (per-record rows for the table) and a full <strong>data file</strong> in S3, and records one manifest row that also carries the month's rolled-up snapshot (credits, tokens, requests, per-feature and per-user totals). Files are uploaded before anything is deleted, so data is never at risk, and the store is roughly 10× smaller once compressed.</p>
        </div>

        <div class="sz-callout sz-callout-tip">
          <div class="sz-callout-label">No toggle needed — the Period does it</div>
          <p>Unlike Audit Trail, Email Logs, and SCIM (which use an <em>Include Archived</em> opt-in), AI Usage pulls archived data in <strong>automatically</strong> based on the Period you pick. When your range reaches into archived months, the page transparently includes them — you never lose historical usage, and there is nothing extra to switch on.</p>
        </div>

        <h3>How each part reads the archive</h3>
        <table>
          <thead>
            <tr>
              <th>Part of the page</th>
              <th>How archived months are included</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Summary cards &amp; Trend</strong></td><td>Read each archived month's rolled-up <strong>snapshot</strong> straight from the manifest in the database — no S3 download — and fold it into the totals and one trend bucket per month. Fast, and totals stay correct after the raw rows are gone.</td></tr>
            <tr><td><strong>Records table</strong></td><td>When the range overlaps archived months, the archived per-record rows are read from the S3 <strong>index files</strong> and merged with the live records, then sorted and paginated as one list.</td></tr>
            <tr><td><strong>Recent-only range</strong></td><td>If the selected range doesn't reach any archived month, the cold tier is skipped entirely and only the live database is queried.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Common use cases</h2>
        <div class="sz-use-cases">
          <div class="sz-use-case">
            <div class="sz-use-case-title">See which feature is burning the most credits</div>
            <div class="sz-use-case-flow">
              Set the Period to Last 30 days and read the <strong>Highest Usage Feature</strong> card, then switch the trend to <strong>Credits</strong> to see how that spend is trending.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Investigate one user's consumption</div>
            <div class="sz-use-case-flow">
              Search the user's name or email in the Records table, then open the eye drawer on a row to see the per-call breakdown — stage, model, tokens, and credits for each underlying call.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Review usage from several months ago</div>
            <div class="sz-use-case-flow">
              Pick a Custom range (or Last 1 year / All time) covering the older period. The cards and trend fold in the archived months' snapshots and the table merges the archived records automatically — no toggle required.
            </div>
          </div>
        </div>
      </section>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Tip</div>
        <p>Credits and tokens are two different lenses on the same activity — credits show what a call <em>costs</em>, tokens show how much text moved. Use the trend metric switch to flip between them when you're diagnosing a spike.</p>
      </div>

    </div>`,
  },

  "ai-cost-tracking": {
    title: "AI Cost Tracking",
    subtitle: "Cross-Tenant AI Usage & Cost",
    color: "emerald",
    badge: "Analytics & Reports",
    description:
      "A cross-tenant dashboard for AI spend — total cost, requests, and tokens broken down by tenant, service category, model, and feature, with a cost trend chart and per-item drill-down. View amounts in dollars or credits, export a branded PDF, and read costs from a pre-aggregated rollup that refreshes every ~15 minutes for fast response. Management-tenant only.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p><strong>AI Cost Tracking</strong> shows how much every tenant is spending on AI across the platform — LLM calls, image/video generation, embeddings, speech, web search, storage, and more. It answers "which tenant is spending, on what, on which model, and how is that trending?" and lets you export a branded PDF or switch the whole page between dollars and credits. All figures are aggregated at the tenant level — no individual user details are shown.</p>
      </div>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Management-tenant only</div>
        <p>This dashboard is <strong>only accessible from the management tenant</strong> and is additionally gated by the <code>admin_ai_cost_tracking_read</code> / <code>admin_ai_cost_tracking_write</code> role permission. On any other tenant the menu item is hidden, the route redirects to <em>Access Denied</em>, and the backend rejects every request that isn't from the management context.</p>
      </div>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/a2858a8d-c173-4e04-bd58-665806a7b04f.png" alt="AI Cost Tracking dashboard" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: AI Cost Tracking — KPIs, trend, and breakdowns</p></div>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">How the data stays fast — the ~15-minute sync</div>
        <p>Every AI call on the platform is logged as an individual raw usage record, which quickly grows to millions of rows — far too many to scan live on every page load. Instead, a background job runs <strong>every ~15 minutes per tenant</strong> and rolls those raw records up into small <strong>pre-aggregated daily buckets</strong> (grouped by tenant, feature, service, model, and day). The dashboard reads <em>only these compact pre-summarised buckets</em>, never the raw rows — which is what keeps response time low even across all tenants and all history.</p>
        <p>The rollup is <strong>incremental</strong>: each cycle re-processes only tenants that had new AI activity since the last run and skips the rest, so it stays cheap. Because of this, numbers can lag real usage by up to ~15 minutes. Two things keep the view current: the dashboard <strong>auto-refreshes when you return to the tab</strong>, and the <strong>Sync Data</strong> button triggers an immediate rollup instead of waiting for the next cycle.</p>
      </div>

      <section>
        <h2>Header Actions</h2>
        <table>
          <thead>
            <tr>
              <th>Action</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Filters</strong></td><td>Show / hide the filter panel (open by default).</td></tr>
            <tr><td><strong>PDF</strong></td><td>Generate a branded PDF of the current view server-side and open it in a new tab. Respects the active filters and the selected dollar/credit format.</td></tr>
            <tr><td><strong>History</strong></td><td>Open the Export History modal — who exported a PDF, when, and for which scope.</td></tr>
            <tr><td><strong>Credit Rate</strong></td><td>Open the popup that sets how many US dollars one credit is worth (drives the Credits display format).</td></tr>
            <tr><td><strong>Sync Data</strong></td><td>Run the rollup immediately rather than waiting for the next ~15-minute cycle. Shows a toast with how many tenants were updated, then reloads the dashboard.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Filters</h2>
        <table>
          <thead>
            <tr>
              <th>Filter</th>
              <th>Options</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Date range</strong></td><td>Last 7 days, Last 30 days, Last 90 days, Last 6 months, Last 1 year, All time (default), or a Custom From/To range. Future dates are disabled.</td></tr>
            <tr><td><strong>Tenant</strong></td><td>All tenants (default) or a single tenant. Selecting one tenant unlocks the per-item Cost Detail table and unit economics.</td></tr>
            <tr><td><strong>Category</strong></td><td>All categories or one of: Text Generation, Image Generation, Embeddings, Text to Speech, Speech to Text, Web Search, Video, Storage, Knowledge Store, Other.</td></tr>
            <tr><td><strong>Cost Format</strong></td><td>Dollars ($) or Credits. Credits require a Credit Rate to be set; until then values stay in dollars with a hint. This is display-only — it never changes the underlying data.</td></tr>
          </tbody>
        </table>
        <p><strong>Clear all</strong> resets every filter back to All time / All tenants / All categories.</p>
      </section>

      <section>
        <h2>KPI Cards</h2>
        <p>Four headline tiles reflecting the current filters:</p>
        <table>
          <thead>
            <tr>
              <th>Card</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Total Cost</strong></td><td>Total spend for the selected scope, in $ or credits. Shows a period-over-period change arrow — an increase is red, a decrease green (lower spend is "good").</td></tr>
            <tr><td><strong>Requests</strong></td><td>Number of AI calls.</td></tr>
            <tr><td><strong>Input Tokens</strong></td><td>Prompt tokens sent to the models.</td></tr>
            <tr><td><strong>Output Tokens</strong></td><td>Completion tokens generated by the models.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Cost Trend</h2>
        <p>A line chart of spend over time. A toggle switches the metric between <strong>Cost</strong>, <strong>Requests</strong>, and <strong>Tokens</strong>. The time grouping (daily / weekly / monthly) is chosen automatically from the date range so the chart stays readable — short ranges plot daily, longer ranges roll up to weekly then monthly.</p>
        <ul>
          <li><strong>All tenants:</strong> one line for each of the top 6 tenants by spend, plus a single "Others" line that combines every remaining tenant.</li>
          <li><strong>Single tenant:</strong> a single aggregate line for that tenant.</li>
        </ul>
      </section>

      <section>
        <h2>Breakdowns</h2>
        <table>
          <thead>
            <tr>
              <th>Panel</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Cost by Service Category</strong></td><td>Doughnut chart splitting spend across the service categories (Text Generation, Image Generation, Embeddings, etc.).</td></tr>
            <tr><td><strong>Cost by Model</strong></td><td>Table of each model with its category, input/output tokens, and cost.</td></tr>
            <tr><td><strong>Cost by Feature</strong></td><td>What produced the spend — Course, Certification, Learning Path, Trending Topic, AI Assistant, Avatar Chat, Document Store, Other — with a share bar, request count, and cost. When a single tenant is selected, an extra <strong>Avg / item</strong> column shows unit economics (cost ÷ items generated, e.g. cost per course).</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Cost by Tenant (leaderboard)</h2>
        <p>Shown only in the <strong>All tenants</strong> view. Tenants are ranked by spend (top 3 get medal-colored badges) with a share bar, requests, input/output tokens, and cost. Tenants with no AI usage yet are included with zero cost and sort to the bottom, so the full tenant list is always visible. Paginated (10 / 20 / 50 per page).</p>
      </section>

      <section>
        <h2>Cost Detail (per-item drill-down)</h2>
        <p>Shown only when a <strong>single tenant</strong> is selected. Lists each individual item (a specific course, certification, document store, etc.) with its type, date, tokens, and cost. Click a row to expand a breakdown of that item's cost by stage / service. Has its own category filter and pagination (20 per page).</p>
      </section>

      <section>
        <h2>Credit Rate</h2>
        <p>The <strong>Credit Rate</strong> popup sets a single platform-wide conversion: <strong>1 credit = $X USD</strong> (decimals allowed, must be greater than 0). Once set, choosing the <strong>Credits</strong> cost format converts every dollar amount on the page (and in the PDF) into credits. Saving updates the display immediately. This is a management-only setting.</p>

        <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/447a261a-62e4-4a01-9d78-08da6115828f.png" alt="Credit Rate popup" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Credit Rate — set the USD value of 1 credit</p></div>
      </section>

      <section>
        <h2>PDF Export &amp; History</h2>
        <p>The <strong>PDF</strong> button builds a branded report of the current view server-side, stores it, and opens it in a new tab — using the active filters and the chosen dollar/credit format. Every export is logged.</p>
        <p>Open <strong>History</strong> to see the export log: the user who exported, when, the scope (date range · tenant · category), and a link to re-open each generated PDF. Paginated.</p>

        <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/2af7abda-0ebc-4c90-992d-4ca31a4e919e.png" alt="AI Cost Tracking export history" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Export History — who exported, when, and scope</p></div>
      </section>

      <section>
        <h2>Common use cases</h2>
        <div class="sz-use-cases">
          <div class="sz-use-case">
            <div class="sz-use-case-title">Find the biggest AI spenders</div>
            <div class="sz-use-case-flow">
              Keep the view on <strong>All tenants</strong> and <strong>All time</strong>, then read the <strong>Cost by Tenant</strong> leaderboard. The top ranks are your heaviest AI consumers — click a tenant in the filter to drill into what's driving their spend.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Understand one tenant's unit economics</div>
            <div class="sz-use-case-flow">
              Select a single tenant, then check <strong>Cost by Feature</strong> — the <strong>Avg / item</strong> column reveals cost per course, per certification, etc. Use <strong>Cost Detail</strong> to see the exact items and expand any row for its stage-by-stage breakdown.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Bill a customer in credits</div>
            <div class="sz-use-case-flow">
              Set the <strong>Credit Rate</strong> (1 credit = $X), switch <strong>Cost Format</strong> to Credits, filter to the tenant and the billing period, and export a <strong>PDF</strong> — the report shows spend in credits ready to share.
            </div>
          </div>
        </div>
      </section>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Tip</div>
        <p>If a recent AI job isn't reflected yet, click <strong>Sync Data</strong> — the dashboard reads a rollup that only refreshes every ~15 minutes, so a manual sync (or just revisiting the tab) pulls the latest numbers without waiting for the next cycle.</p>
      </div>

    </div>`,
  },
};
